import { numberHelpers } from '@approvalmax/utils';
import uniqueId from 'lodash/uniqueId';

import { NetSuiteJournalEntryLineExpanded } from '../../data/types';
import {
    calculateLineAmountsBasedOnAmount,
    calculateLineAmountsBasedOnGrossAmount,
    checkJournalEntryLineIsEmpty,
} from '../../helpers';

export const createDefaultJournalEntryLine = (lineNumber: number) => {
    return {
        lineNumber,
        account: null,
        debitAmount: null,
        creditAmount: null,
        taxCode: null,
        taxRate: null,
        taxAmount: null,
        taxAccount: null,
        grossAmount: null,
        class: null,
        department: null,
        location: null,
        customFields: [],
        isEmpty: false,
        checked: false,
        memo: null,
        customer: null,
        amortizationSchedule: null,
        amortizationStartDate: null,
        amortizationEndDate: null,
        amortizationResidual: null,
        id: uniqueId('netSuiteJournalEntryLine-'),
    };
};

export const updateJournalEntryLineFieldValue = <K extends keyof NetSuiteJournalEntryLineExpanded>(
    line: NetSuiteJournalEntryLineExpanded,
    key: K,
    value: NetSuiteJournalEntryLineExpanded[K],
    currencyPrecision: number
): NetSuiteJournalEntryLineExpanded => {
    let newLine = {
        ...line,
        [key]: value,
    };

    if (key === 'taxCode') {
        const taxRate =
            newLine.taxCode && 'rate' in newLine.taxCode && numberHelpers.isNumber(newLine.taxCode.rate)
                ? newLine.taxCode.rate
                : null;

        const amount = newLine.debitAmount && newLine.debitAmount > 0 ? newLine.debitAmount : newLine.creditAmount;
        const { taxAmount, grossAmount } = calculateLineAmountsBasedOnAmount(amount, taxRate, currencyPrecision);

        newLine = {
            ...newLine,
            taxRate,
            taxAmount,
            grossAmount,
        };
    }

    if (key === 'debitAmount') {
        const { taxAmount, grossAmount } = calculateLineAmountsBasedOnAmount(
            newLine.debitAmount,
            newLine.taxRate,
            currencyPrecision
        );

        newLine = {
            ...newLine,
            creditAmount: null,
            taxAmount,
            grossAmount,
        };
    }

    if (key === 'creditAmount') {
        const { taxAmount, grossAmount } = calculateLineAmountsBasedOnAmount(
            newLine.creditAmount,
            newLine.taxRate,
            currencyPrecision
        );

        newLine = {
            ...newLine,
            debitAmount: null,
            taxAmount,
            grossAmount,
        };
    }

    if (key === 'grossAmount') {
        const { taxAmount, amount, grossAmount } = calculateLineAmountsBasedOnGrossAmount(
            newLine.grossAmount,
            newLine.taxRate,
            currencyPrecision
        );

        if (newLine.debitAmount && newLine.debitAmount > 0) {
            newLine.debitAmount = amount;
        } else {
            newLine.creditAmount = amount;
        }

        newLine = {
            ...newLine,
            taxAmount,
            grossAmount,
        };
    }

    newLine.isEmpty = checkJournalEntryLineIsEmpty(newLine);

    return newLine;
};
